import { useTranslation } from '@thoughtspot/i18n';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { getMutedAlertSVGPath } from '@thoughtspot/radiant-react/widgets/alert/alert-utils';
import { Avatar } from '@thoughtspot/radiant-react/widgets/avatar';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Tab, TabHeaderHorizontalAlignment, TabTrayLayout, TabTraySize, } from '@thoughtspot/radiant-react/widgets/tab';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getGuidsToSkip, hasPrivilege, PRIVILEGES, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { OrgManagementTranslationKeys } from '../../constants';
import { StepFeatureConfig } from '../wizard/step-feature-config';
import styles from './org-detail-modal.module.scss';
import { CALLOSUM_BASE_PATH, DETAIL_BATCH_SIZE, DetailTab, formatDate, TAB_TYPE_MAP, } from './org-detail-modal.utils';
export const OrgDetailModal = ({ isOpen, org, orgDetailData, orgDetailLoading, onGetOrgDetail, onEdit, onClose, }) => {
    const { t } = useTranslation();
    const isAdmin = hasPrivilege(PRIVILEGES.ADMINISTRATION);
    const getDefaultTab = () => {
        if (isAdmin)
            return DetailTab.USERS;
        return DetailTab.GROUPS;
    };
    const defaultTab = getDefaultTab();
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [offset, setOffset] = useState(0);
    const [accumulatedUsers, setAccumulatedUsers] = useState([]);
    const [accumulatedGroups, setAccumulatedGroups] = useState([]);
    const [loadedKey, setLoadedKey] = useState('');
    const isLastBatchRef = useRef(false);
    const currentKey = isOpen && org ? `${org.id}-${activeTab}-${offset}` : '';
    useEffect(() => {
        if (isOpen) {
            setActiveTab(defaultTab);
        }
    }, [isOpen, org?.id]);
    useEffect(() => {
        setOffset(0);
        setAccumulatedUsers([]);
        setAccumulatedGroups([]);
        isLastBatchRef.current = false;
    }, [activeTab, org?.id]);
    useEffect(() => {
        if (org && isOpen) {
            const isFeatureTab = activeTab === DetailTab.FEATURE_FLAGS;
            onGetOrgDetail({
                orgid: parseInt(org.id, 10),
                type: TAB_TYPE_MAP[activeTab],
                batchsize: isFeatureTab ? -1 : DETAIL_BATCH_SIZE,
                offset: isFeatureTab ? -1 : offset,
            });
        }
    }, [activeTab, org, isOpen, offset]);
    useEffect(() => {
        if (!orgDetailLoading && currentKey && orgDetailData) {
            setLoadedKey(currentKey);
        }
    }, [orgDetailLoading, orgDetailData, currentKey]);
    const isLoading = orgDetailLoading || (currentKey !== '' && currentKey !== loadedKey);
    useEffect(() => {
        const newUsers = orgDetailData?.Data?.users?.data ?? [];
        const newGroups = orgDetailData?.Data?.userGroups?.data ?? [];
        if (activeTab === DetailTab.USERS) {
            isLastBatchRef.current =
                orgDetailData?.Data?.users?.isLastBatch ?? true;
            if (newUsers.length > 0) {
                setAccumulatedUsers(prev => offset === 0 ? newUsers : [...prev, ...newUsers]);
            }
        }
        else if (activeTab === DetailTab.GROUPS) {
            isLastBatchRef.current =
                orgDetailData?.Data?.userGroups?.isLastBatch ?? true;
            if (newGroups.length > 0) {
                setAccumulatedGroups(prev => offset === 0 ? newGroups : [...prev, ...newGroups]);
            }
        }
    }, [orgDetailData]);
    const handleTabChange = useCallback((tabId) => {
        setActiveTab(tabId);
    }, []);
    const handleListScroll = useCallback((e) => {
        if (isLoading || isLastBatchRef.current)
            return;
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
            setOffset(prev => prev + DETAIL_BATCH_SIZE);
        }
    }, [isLoading]);
    const users = accumulatedUsers;
    const skipIds = useMemo(() => new Set(getGuidsToSkip()), []);
    const userGroups = useMemo(() => accumulatedGroups.filter(g => !skipIds.has(g.id ?? '')), [accumulatedGroups, skipIds]);
    const featureConfigs = useMemo(() => {
        const seen = new Set();
        return (orgDetailData?.Data?.featureFlags ?? []).flatMap(group => (group?.features ?? []).flatMap(feature => {
            if (seen.has(feature?.featureId))
                return [];
            seen.add(feature?.featureId);
            return [
                {
                    configName: feature?.featureId ?? '',
                    orgIds: [],
                    elementType: (feature?.elementType ??
                        'toggle'),
                    value: feature?.elementValue ?? 'false',
                    category: group?.groupName ?? 'Other',
                },
            ];
        }));
    }, [orgDetailData]);
    if (!org)
        return null;
    const emptyState = (React.createElement(Vertical, { className: styles.emptyState },
        React.createElement("img", { src: getMutedAlertSVGPath(ErrorCode.GLOBAL_GENERAL_WARNING), alt: "", className: styles.emptyStateImg }),
        React.createElement(Typography, { variant: "h4", overrideTag: "div", noMargin: true }, t(OrgManagementTranslationKeys.DETAIL_MODAL_NONE_ASSIGNED))));
    return (React.createElement(Modal, { isOpen: isOpen, size: ModalSize.MEDIUM3, title: t(OrgManagementTranslationKeys.DETAIL_MODAL_TITLE), onDismiss: onClose, dismissButton: {
            text: t(OrgManagementTranslationKeys.ACTION_EDIT),
            onClick: onEdit,
        }, confirmButton: {
            text: t(OrgManagementTranslationKeys.DETAIL_MODAL_CLOSE),
            onConfirm: onClose,
        }, alwaysMaxHeight: true, className: styles.orgDetailModal, customFooterClassName: styles.orgDetailFooter, "data-testid": "org-detail-modal" },
        React.createElement(Horizontal, { className: styles.orgDetailBody },
            React.createElement(Vertical, { className: styles.leftPanel },
                React.createElement(Typography, { variant: "h4", overrideTag: "div", className: styles.orgName, "data-testid": "detail-org-name" }, org.name),
                React.createElement(Typography, { variant: "body-normal", overrideTag: "div", color: Colors.info, className: styles.orgDescription, "data-testid": "detail-org-description" }, org.description),
                React.createElement(Horizontal, { className: styles.metaRow, vAlignContent: "center" },
                    React.createElement(Typography, { variant: "p", overrideTag: "span", color: Colors.base, className: styles.metaLabel }, t(OrgManagementTranslationKeys.DETAIL_MODAL_CREATED_BY)),
                    React.createElement(Horizontal, { spacing: "b", vAlignContent: "center", "data-testid": "detail-org-creator" },
                        React.createElement(Avatar, { name: org.creator.name, src: `${CALLOSUM_BASE_PATH}/image/profile/${org.creator.id}`, size: "s" }),
                        React.createElement(Typography, { variant: "body-normal", overrideTag: "span", noMargin: true }, org.creator.name))),
                React.createElement(Horizontal, { className: styles.metaRow, vAlignContent: "center" },
                    React.createElement(Typography, { variant: "p", overrideTag: "span", color: Colors.base, className: styles.metaLabel }, t(OrgManagementTranslationKeys.DETAIL_MODAL_CREATED_ON)),
                    React.createElement(Typography, { variant: "body-normal", overrideTag: "span", color: Colors.info, "data-testid": "detail-org-created" }, formatDate(org.createdTimestamp)))),
            React.createElement(Vertical, { className: styles.rightPanel },
                React.createElement(Tab, { layout: TabTrayLayout.Horizontal, size: TabTraySize.Medium, selectedTabId: activeTab, headerAlignment: TabHeaderHorizontalAlignment.LEFT, "data-testid": "detail-modal-tabs" },
                    isAdmin && (React.createElement(Tab.Item, { id: DetailTab.USERS, tabBottomBorder: true, name: t(OrgManagementTranslationKeys.COL_USERS), onTabItemClick: () => handleTabChange(DetailTab.USERS), "data-testid": "detail-modal-tab-users" },
                        React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading, className: styles.tabLoader }, !isLoading &&
                            (users.length > 0 ? (React.createElement(Vertical, { className: styles.listContainer, "data-testid": "detail-org-users", onScroll: handleListScroll }, users.map(user => (React.createElement(Horizontal, { key: user?.id, spacing: "b", vAlignContent: "center", className: styles.listItem },
                                React.createElement(Avatar, { name: user?.name ?? '', src: `${CALLOSUM_BASE_PATH}/image/profile/${user?.id}`, size: "s" }),
                                React.createElement(Typography, { variant: "body-normal", overrideTag: "span", noMargin: true, preserveWhitespace: false, className: styles.listItemText }, user?.name)))))) : (emptyState))))),
                    React.createElement(Tab.Item, { id: DetailTab.GROUPS, tabBottomBorder: true, name: t(OrgManagementTranslationKeys.COL_GROUPS), onTabItemClick: () => handleTabChange(DetailTab.GROUPS), "data-testid": "detail-modal-tab-groups" },
                        React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading, className: styles.tabLoader }, !isLoading &&
                            (userGroups.length > 0 ? (React.createElement(Vertical, { className: styles.listContainer, "data-testid": "detail-org-groups", onScroll: handleListScroll }, userGroups.map(group => (React.createElement(Horizontal, { key: group?.id, vAlignContent: "center", shrink: false, className: styles.listItemPlain },
                                React.createElement(Typography, { variant: "body-normal", color: Colors.grayLight, overrideTag: "span", noMargin: true, preserveWhitespace: false, className: styles.listItemText }, group?.name)))))) : (emptyState)))),
                    isAdmin && (React.createElement(Tab.Item, { id: DetailTab.FEATURE_FLAGS, tabBottomBorder: true, name: t(OrgManagementTranslationKeys.DETAIL_MODAL_TAB_FEATURE_FLAGS), onTabItemClick: () => handleTabChange(DetailTab.FEATURE_FLAGS), "data-testid": "detail-modal-tab-features" }, featureConfigs.length > 0 || isLoading ? (React.createElement(StepFeatureConfig, { features: featureConfigs, loading: isLoading, searchTerm: "", readOnly: true })) : (emptyState))))))));
};
//# sourceMappingURL=org-detail-modal.js.map